import { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import {
  FaArrowRight, FaCheck, FaChevronDown, FaCode, FaCropAlt,
  FaExternalLinkAlt, FaFileImage, FaFilePdf, FaImage, FaInfoCircle,
  FaRegFileAlt, FaSyncAlt, FaFont,
} from "react-icons/fa";
import { uploadHandoutFigure } from "../../api/assets";
import { attachHandoutFigure, reconcileHandoutQuestionIds, matchFigureQuestionId } from "../../features/document-processing/handoutFigures";
import { isPdfFile } from "./pdfBatchExtraction";
import { renderPdfPagesAsPng } from "./pdfPreview";

import { usePointerDragAutoScroll } from "../../shared/dragAutoScroll";

import BatchHandoutFigures from "./BatchHandoutFigures";

const whole = { x: 0, y: 0, w: 1, h: 1 };
const normalizedCandidateCrop = (candidate) => {
  const box = candidate?.bbox;
  if (!Array.isArray(box) || box.length !== 4 || !box.every(Number.isFinite)) return null;
  const [x, y, w, h] = box;
  if (x < 0 || y < 0 || w <= 0 || h <= 0 || x + w > 1 || y + h > 1) return null;
  return { x, y, w, h };
};
const questionSummary = (question) => question.preview
  .replace(/^第\s*[\d一二三四五六七八九十百]+\s*題\s*/, "")
  .replace(/^題目\s*[：:]\s*/, "")
  .replace(/\[附圖待處理[^\]]*\]/g, "")
  .trim();
const boundedRequest = async (promise, milliseconds = 90000) => {
  let timer;
  try {
    return await Promise.race([promise, new Promise((_, reject) => {
      timer = setTimeout(() => reject(new Error("服務回應逾時，請稍後重試。")), milliseconds);
    })]);
  } finally { clearTimeout(timer); }
};
const croppedOcrText = (value) => String(value || "")
  .replace(/^--- Page 1 ---\s*\n?/, "").trim();

export default function HandoutFigureWorkspace({ files, text, onChangeText, figureCandidates = [], authToken, disabled,
  startPdfExtractJob, getPdfExtractJob, previewMatplotlibCode, imageTextMode = "local_ocr" }) {
  const autoScroll = usePointerDragAutoScroll();
  const questionRegistry = useRef([]);
  const questionSequence = useRef(0);
  const questions = useMemo(() => {
    const next = reconcileHandoutQuestionIds(text, questionRegistry.current, () => `handout-question-${++questionSequence.current}`);
    questionRegistry.current = next; return next;
  }, [text]);
  const candidates = useMemo(() => figureCandidates.filter((candidate) =>
    Number.isInteger(candidate?.page) && candidate.page > 0 && normalizedCandidateCrop(candidate),
  ), [figureCandidates]);
  const [questionId, setQuestionId] = useState("");
  const [candidateIndex, setCandidateIndex] = useState("");
  const [role, setRole] = useState("題目");
  const [fileIndex, setFileIndex] = useState(0);
  const [page, setPage] = useState(1);
  const [source, setSource] = useState("");
  const [crop, setCrop] = useState(whole);
  const [cropTouched, setCropTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [messageTone, setMessageTone] = useState("info");
  const [result, setResult] = useState(null);
  const imageRef = useRef(null);
  const dragRef = useRef(null);
  const requestRef = useRef(0);
  const question = questions.find((item) => item.id === questionId) || questions[0];
  const file = files[fileIndex];
  const hasCropSelection = cropTouched || (candidateIndex !== "" &&
    candidates[Number(candidateIndex)]?.page === Number(page));

  useEffect(() => () => { requestRef.current += 1; }, []);
  useEffect(() => () => { if (source) URL.revokeObjectURL(source); }, [source]);
  useEffect(() => {
    autoScroll.stop(); dragRef.current = null;
    requestRef.current += 1;
    const candidate = candidates[Number(candidateIndex)];
    setSource(""); setResult(null); setBusy(false);
    setCropTouched(false);
    setCrop(candidateIndex !== "" && candidate?.page === Number(page)
      ? normalizedCandidateCrop(candidate) || whole : whole);
    setMessage("");
    // Candidate changes on the same page only adjust the crop; keep the loaded preview.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file, page]);
  useEffect(() => { setQuestionId(""); setFileIndex(0); setCandidateIndex(""); }, [files]);

  const chooseCandidate = (value) => {
    setCandidateIndex(value);
    if (value === "") {
      setCrop(whole);
      setCropTouched(false);
      setResult(null);
      return;
    }
    const candidate = candidates[Number(value)];
    if (!candidate) return;
    const matchedId = matchFigureQuestionId(questions, { ...candidate, fileIndex }, files.length);
    if (matchedId) setQuestionId(matchedId);
    setRole("題目");
    setPage(candidate.page);
    setCrop(normalizedCandidateCrop(candidate) || whole);
    setResult(null);
  };

  const loadSource = async () => {
    if (!file) return;
    const token = ++requestRef.current;
    setMessageTone("info");
    setBusy(true); setResult(null); setMessage("正在載入原圖…");
    try {
      const blob = isPdfFile(file)
        ? (await renderPdfPagesAsPng(file, [{ id: "figure", pageIndex: Number(page) - 1 }]))[0].blob
        : file;
      if (token !== requestRef.current) return;
      const candidate = candidates[Number(candidateIndex)];
      setSource(URL.createObjectURL(blob));
      setCrop(candidateIndex !== "" && candidate?.page === Number(page)
        ? normalizedCandidateCrop(candidate) || whole : whole);
      setMessage("請手動框選需要處理的局部內容；若要整張，按「使用整張」。請排除姓名等個人資料。");
    } catch (error) {
      if (token === requestRef.current) {
        setMessageTone("error");
        setMessage(`無法載入頁面，請確認頁碼。${error.message || ""}`);
      }
    } finally { if (token === requestRef.current) setBusy(false); }
  };

  const makeCrop = async () => {
    const img = imageRef.current;
    if (!img?.naturalWidth) throw new Error("原圖尚未載入完成。");
    const width = Math.max(1, Math.round(img.naturalWidth * crop.w));
    const height = Math.max(1, Math.round(img.naturalHeight * crop.h));
    if (width < 10 || height < 10) throw new Error("框選範圍太小，請重新選取。");
    const scale = Math.min(1, 3000 / Math.max(width, height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(width * scale); canvas.height = Math.round(height * scale);
    canvas.getContext("2d").drawImage(img, img.naturalWidth * crop.x, img.naturalHeight * crop.y,
      width, height, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
    if (!blob) throw new Error("無法產生裁切圖片。");
    return { file: new File([blob], "handout-figure.png", { type: "image/png" }), preview: canvas.toDataURL("image/png") };
  };

  const process = async (kind) => {
    if (!hasCropSelection) return;
    const token = ++requestRef.current;
    const snapshot = { text, question, role };
    setMessageTone("info");
    setBusy(true); setResult(null); setMessage(kind === "url" ? "正在上傳框選附圖…"
      : kind === "text" ? "正在建立局部 OCR 工作…" : "正在建立附圖轉 Python 工作…");
    try {
      const cropped = await makeCrop();
      if (token !== requestRef.current) return;
      let figure;
      if (kind === "url") {
        const uploaded = await uploadHandoutFigure({ file: cropped.file, authToken });
        if (!uploaded.success) throw new Error(uploaded.error);
        figure = { kind, url: uploaded.url, preview: cropped.preview };
      } else {
        if (!startPdfExtractJob || !getPdfExtractJob || (kind === "python" && !previewMatplotlibCode)) {
          throw new Error("目前無法使用附圖處理服務，請重新整理。");
        }
        const started = await boundedRequest(startPdfExtractJob(cropped.file,
          kind === "text" ? imageTextMode : "matplotlib", kind === "text" ? "standard" : "ultra"));
        if (!started.success || !started.job?.id) throw new Error(started.error || "無法建立工作。");
        let job = started.job;
        const deadline = kind === "python" ? Date.now() + 15 * 60 * 1000 : Infinity;
        let failures = 0;
        while (["queued", "running"].includes(job.status)) {
          if (token !== requestRef.current) return;
          if (Date.now() > deadline) throw new Error("附圖工作等待逾時，請稍後重試。");
          setMessage(job.status === "queued" ? (kind === "text" ? "局部 OCR 排隊中…" : "附圖轉 Python 排隊中…")
            : "正在辨識框選內容…");
          await new Promise((resolve) => setTimeout(resolve, 1500));
          if (token !== requestRef.current) return;
          const polled = await boundedRequest(getPdfExtractJob(job.id));
          if (!polled.success || !polled.job) {
            if (++failures < 3) continue;
            throw new Error(polled.error || "工作查詢失敗。");
          }
          failures = 0; job = polled.job;
        }
        if (job.status !== "success" || !job.text?.trim()) throw new Error(job.error || "框選內容沒有辨識到文字或圖形。");
        if (token !== requestRef.current) return;
        if (kind === "text") {
          figure = { kind, text: croppedOcrText(job.text), original: cropped.preview };
          if (!figure.text) throw new Error("框選區域沒有辨識到文字，請調整範圍後重試。");
        } else {
          // Keep the paid conversion even if rendering fails; retry only rendering.
          setResult({ kind, code: job.text, preview: "", original: cropped.preview, snapshot });
          setMessage("正在產生 Python 預覽…");
          const preview = await boundedRequest(previewMatplotlibCode(job.text));
          if (!preview.success || !preview.image) throw new Error(preview.error || "預覽失敗，程式碼已保留；可重試預覽或改用原圖。");
          figure = { kind, code: job.text, preview: preview.image, original: cropped.preview };
        }
      }
      if (token !== requestRef.current) return;
      setMessageTone("success");
      setResult({ ...figure, snapshot }); setMessage("請核對預覽，再加入題目；尚未存入題庫。");
    } catch (error) {
      if (token === requestRef.current) {
        setMessageTone("error");
        setMessage(error.message || "附圖處理失敗。");
      }
    } finally { if (token === requestRef.current) setBusy(false); }
  };

  const retryPreview = async () => {
    if (!result?.code) return;
    const token = ++requestRef.current;
    setMessageTone("info");
    setBusy(true); setMessage("正在重新產生預覽，不會再次辨識圖片…");
    try {
      const preview = await boundedRequest(previewMatplotlibCode(result.code));
      if (!preview.success || !preview.image) throw new Error(preview.error || "預覽失敗，程式碼仍保留在下方。");
      if (token === requestRef.current) {
        setResult((current) => ({ ...current, preview: preview.image }));
        setMessageTone("success");
        setMessage("請核對預覽，再加入題目；尚未存入題庫。");
      }
    } catch (error) {
      if (token === requestRef.current) {
        setMessageTone("error");
        setMessage(error.message);
      }
    } finally { if (token === requestRef.current) setBusy(false); }
  };

  const point = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    return { x: Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width)),
      y: Math.max(0, Math.min(1, (event.clientY - rect.top) / rect.height)) };
  };
  const selectPoint = (event) => {
    if (!dragRef.current || busy || disabled) return;
    const end = point(event), start = dragRef.current;
    setCrop({ x: Math.min(start.x, end.x), y: Math.min(start.y, end.y),
      w: Math.abs(start.x - end.x), h: Math.abs(start.y - end.y) });
    setResult(null);
  };
  const apply = () => {
    if (!(result?.kind === "text" ? result.text?.trim() : result?.preview) || result.snapshot.text !== text) return;
    const snapshot = result.snapshot;
    onChangeText((current) => current === snapshot.text
      ? attachHandoutFigure(current, snapshot.question, snapshot.role, result) : current);
    setMessageTone("success");
    setResult(null); setMessage("框選內容已加入解析文字；送至大量入題並儲存後才會成為題庫資料。");
  };

  const activeStep = !question ? 0 : result ? 2 : source ? 1 : 0;

  return (
    <Workspace aria-label="講義附圖處理" aria-busy={busy || disabled}>
      <WorkspaceHeader>
        <HeadingIcon><FaImage aria-hidden="true" /></HeadingIcon>
        <HeadingCopy>
          <h3>逐題附圖</h3>
          <p>可自動偵測整份講義附圖，或手動框選局部內容；使用獨立 UI ID 對應題目。</p>
        </HeadingCopy>
        <Badge $active={questions.length > 0}>
          {busy || disabled ? <><Spinner aria-hidden="true" /> 處理中</> : questions.length ? `${questions.length} 題可加入附圖` : "等待解析"}
        </Badge>
      </WorkspaceHeader>

      <Steps aria-label="附圖處理步驟">
        {["選題與原稿", "框選與保存", "核對後加入"].map((label, index) => (
          <Step key={label} $active={activeStep === index} $done={activeStep > index}
            aria-current={activeStep === index ? "step" : undefined}>
            <span>{activeStep > index ? <FaCheck aria-hidden="true" /> : `0${index + 1}`}</span>
            {label}
          </Step>
        ))}
      </Steps>

      {!questions.length ? (
        <EmptyState>
          <EmptyIcon><FaRegFileAlt aria-hidden="true" /><FaImage aria-hidden="true" /></EmptyIcon>
          <div>
            <h4>{disabled ? "正在整理講義題目" : "先解析講義，再加入附圖"}</h4>
            <p>{disabled ? "文字整理完成後，就能逐題框選並保存圖片。" : "在上方加入圖片或 PDF，完成文字解析後，就能在這裡處理附圖。"}</p>
            <FormatHint>手動編輯？用 <code>第 1 題</code> 標示題號，或以獨立一行的 <code>---</code> 分題。</FormatHint>
          </div>
        </EmptyState>
      ) : (
        <>
          <BatchHandoutFigures files={files} text={text} questions={questions} onChangeText={onChangeText} disabled={busy || disabled} startPdfExtractJob={startPdfExtractJob} getPdfExtractJob={getPdfExtractJob} previewMatplotlibCode={previewMatplotlibCode} />
          <fieldset disabled={busy || disabled}>
            <SetupGrid>
              <SetupCard>
                <CardHeading><FaRegFileAlt aria-hidden="true" /><h4>選擇題目</h4></CardHeading>
                <Field>
                  <span>所屬題目</span>
                  <SelectWrap>
                    <select value={question?.id || ""} onChange={(e) => { setQuestionId(e.target.value); setResult(null); }}>
                      {questions.map((item) => <option key={item.id} value={item.id}>{item.label}{item.page ? ` · 第 ${item.page} 頁` : ""} — {questionSummary(item)}</option>)}
                    </select>
                    <FaChevronDown aria-hidden="true" />
                  </SelectWrap>
                </Field>
                {question && <QuestionExcerpt title={questionSummary(question)}>{questionSummary(question)}</QuestionExcerpt>}
                <FieldCaption>附圖位置</FieldCaption>
                <RoleGroup role="group" aria-label="附圖位置">
                  {["題目", "答案", "詳解"].map((item) => (
                    <RoleButton key={item} type="button" aria-pressed={role === item} $selected={role === item}
                      onClick={() => { setRole(item); setResult(null); }}>{item}</RoleButton>
                  ))}
                </RoleGroup>
              </SetupCard>

              <SetupCard>
                <CardHeading>{file && isPdfFile(file) ? <FaFilePdf aria-hidden="true" /> : <FaFileImage aria-hidden="true" />}<h4>選擇原稿</h4></CardHeading>
                {candidates.length > 0 && <Field>
                  <span>偵測到的附圖候選區</span>
                  <SelectWrap>
                    <select aria-label="偵測到的附圖候選區" value={candidateIndex} onChange={(e) => chooseCandidate(e.target.value)}>
                      <option value="">手動框選</option>
                      {candidates.map((candidate, index) => <option key={`${candidate.page}-${index}`} value={index}>
                        第 {candidate.page} 頁 · 候選區 {index + 1}
                      </option>)}
                    </select>
                    <FaChevronDown aria-hidden="true" />
                  </SelectWrap>
                </Field>}
                <Field>
                  <span>來源檔案</span>
                  <SelectWrap>
                    <select value={fileIndex} disabled={!files.length} onChange={(e) => { setFileIndex(Number(e.target.value)); setPage(1); }}>
                      {!files.length && <option value={0}>尚未選擇來源檔案</option>}
                      {files.map((item, i) => <option key={i} value={i}>{i + 1}. {item.name}</option>)}
                    </select>
                    <FaChevronDown aria-hidden="true" />
                  </SelectWrap>
                </Field>
                <SourceHint>{!file ? "請先在上方加入圖片或 PDF 檔案。" : isPdfFile(file) ? "選擇附圖所在頁面，再載入原圖。" : "載入後可框選一張圖，不必上傳整份原稿。"}</SourceHint>
                <SourceActions>
                  {file && isPdfFile(file) && <Field $narrow><span>PDF 頁碼</span><input type="number" min="1" value={page}
                    onChange={(e) => setPage(Math.max(1, Math.floor(Number(e.target.value) || 1)))} /></Field>}
                  <ActionButton type="button" onClick={loadSource} disabled={!file}>
                    <FaImage aria-hidden="true" />載入原圖
                  </ActionButton>
                </SourceActions>
              </SetupCard>
            </SetupGrid>

            <CropSection>
              <SectionHeading><div><FaCropAlt aria-hidden="true" /><h4>手動框選局部內容</h4></div><small>{source ? "拖曳框選需要處理的範圍，其餘頁面沿用整頁解析" : "載入原圖後，即可開始框選"}</small></SectionHeading>
              <CanvasStage $loaded={Boolean(source)}>
                {source ? (
                  <CropFrame onPointerDown={(e) => {
                    if (busy || disabled || e.button !== 0) return;
                    e.currentTarget.setPointerCapture(e.pointerId); dragRef.current = point(e); setResult(null); autoScroll.start(e);
                  }} onPointerMove={selectPoint} onPointerUp={(e) => { selectPoint(e); setCropTouched(true); dragRef.current = null; }}
                    onPointerCancel={() => { dragRef.current = null; }}>
                    <img ref={imageRef} src={source} alt="講義原稿，可拖曳框選附圖" draggable={false} />
                    <Selection style={{ left: `${crop.x * 100}%`, top: `${crop.y * 100}%`, width: `${crop.w * 100}%`, height: `${crop.h * 100}%` }} />
                  </CropFrame>
                ) : (
                  <CanvasPlaceholder><FaCropAlt aria-hidden="true" /><strong>原稿預覽區</strong><span>選擇來源檔案，按「載入原圖」</span></CanvasPlaceholder>
                )}
              </CanvasStage>
              {source && <>
                <CropToolbar><span>框選範圍 <strong>{Math.round(crop.w * 100)}% × {Math.round(crop.h * 100)}%</strong></span>
                  <QuietButton type="button" onClick={() => { setCrop(whole); setCropTouched(true); setResult(null); }}>使用整張</QuietButton>
                </CropToolbar>
                <Disclosure>
                  <summary>精細調整框選範圍</summary>
                  <CropFields>
                    {[["x", "左"], ["y", "上"], ["w", "寬"], ["h", "高"]].map(([key, label]) => (
                      <Field key={key}><span>{label} %</span><input type="number" min="0" max="100" value={Math.round(crop[key] * 100)} onChange={(e) => {
                        const next = { ...crop, [key]: Math.max(0, Math.min(1, Number(e.target.value) / 100)) };
                        next.w = Math.min(next.w, 1 - next.x); next.h = Math.min(next.h, 1 - next.y);
                        setCrop(next); setCropTouched(true); setResult(null);
                      }} /></Field>
                    ))}
                  </CropFields>
                </Disclosure>
              </>}
            </CropSection>

            <SaveSection>
              <SectionHeading><div><h4>選擇處理方式</h4></div><small>{source ? hasCropSelection ? "只處理框選區域，完成後再核對" : "請先手動框選或按「使用整張」" : "請先載入並框選局部內容"}</small></SectionHeading>
              <MethodGrid>
                <MethodCard type="button" aria-label="保留原圖 URL" disabled={!source || !question || !hasCropSelection} onClick={() => process("url")}>
                  <MethodTop><MethodIcon><FaImage aria-hidden="true" /></MethodIcon><MethodTag>保留原始細節</MethodTag></MethodTop>
                  <strong>保留原圖 URL</strong>
                  <p>適合手寫、複雜圖形，完整保留框選內容。</p>
                  <MethodNote>Cloudflare 公開連結 · 不使用 AI 額度</MethodNote>
                  <MethodAction>上傳並預覽<FaArrowRight aria-hidden="true" /></MethodAction>
                </MethodCard>
                <MethodCard type="button" aria-label="轉 Python 圖" $python disabled={!source || !question || !hasCropSelection} onClick={() => process("python")}>
                  <MethodTop><MethodIcon $python><FaCode aria-hidden="true" /></MethodIcon><MethodTag>可編輯與重繪</MethodTag></MethodTop>
                  <strong>轉 Python 圖</strong>
                  <p>適合幾何、座標圖，轉為可修改的繪圖程式。</p>
                  <MethodNote>AI 重新繪製 · 會使用 AI 額度</MethodNote>
                  <MethodAction>轉換並預覽<FaArrowRight aria-hidden="true" /></MethodAction>
                </MethodCard>
                <MethodCard type="button" aria-label="圖轉文字" disabled={!source || !question || !hasCropSelection} onClick={() => process("text")}>
                  <MethodTop><MethodIcon><FaFont aria-hidden="true" /></MethodIcon><MethodTag>補入辨識文字</MethodTag></MethodTop>
                  <strong>圖轉文字</strong>
                  <p>適合整頁辨識漏掉的局部文字；先核對並可修改，再加入所選題目。</p>
                  <MethodNote>使用後端 OCR · 不上傳 Cloudflare</MethodNote>
                  <MethodAction>辨識並核對<FaArrowRight aria-hidden="true" /></MethodAction>
                </MethodCard>
              </MethodGrid>
            </SaveSection>
          </fieldset>

          {message && <StatusNotice role={messageTone === "error" ? "alert" : "status"} $tone={messageTone}>
            {busy ? <Spinner aria-hidden="true" /> : messageTone === "success" ? <FaCheck aria-hidden="true" /> : <FaInfoCircle aria-hidden="true" />}
            <span>{message}</span>
          </StatusNotice>}

          {result && <ReviewSection>
            <SectionHeading><div><FaCheck aria-hidden="true" /><h4>核對後加入</h4></div><Badge $active>尚未加入題目</Badge></SectionHeading>
            <PreviewRow>
              {result.original && <figure><figcaption>框選原圖</figcaption><div><img src={result.original} alt="框選原圖" /></div></figure>}
              {result.preview && <figure><figcaption>{result.kind === "python" ? "Python 預覽" : "原圖預覽"}</figcaption><div><img src={result.preview} alt="待加入的附圖預覽" /></div></figure>}
            </PreviewRow>
            {result.kind === "python" && <Disclosure>
              <summary><FaCode aria-hidden="true" />檢視／校正 Python 程式碼</summary>
              <textarea aria-label="附圖 Python 程式碼" value={result.code} disabled={busy || disabled}
                onChange={(e) => setResult({ ...result, code: e.target.value, preview: "" })} />
            </Disclosure>}
            {result.kind === "text" && <Disclosure open>
              <summary><FaFont aria-hidden="true" />核對／修改框選區域的 OCR 文字</summary>
              <textarea aria-label="框選區域 OCR 文字" value={result.text} disabled={busy || disabled}
                onChange={(e) => setResult({ ...result, text: e.target.value })} />
            </Disclosure>}
            {result.code && !result.preview && <ActionButton type="button" disabled={busy || disabled} onClick={retryPreview}><FaSyncAlt aria-hidden="true" />重試 Python 預覽</ActionButton>}
            {result.url && <OriginalLink href={result.url} target="_blank" rel="noreferrer">開啟原圖 URL<FaExternalLinkAlt aria-hidden="true" /></OriginalLink>}
            {result.snapshot.text !== text && <StatusNotice $tone="error">解析文字已變動，請重新選題並處理附圖，避免加入錯題。</StatusNotice>}
            <ConfirmBar>
              <div><strong>{result.snapshot.question.label} · {result.snapshot.role}{result.kind === "text" ? "文字" : "附圖"}</strong><span>確認後加入解析文字，再送至大量入題儲存。</span></div>
              <ActionButton $primary type="button" disabled={busy || disabled || !(result.kind === "text" ? result.text?.trim() : result.preview) || result.snapshot.text !== text} onClick={apply}>
                <FaCheck aria-hidden="true" />確認加入{result.snapshot.question.label}（{result.snapshot.role}）
              </ActionButton>
            </ConfirmBar>
          </ReviewSection>}
        </>
      )}

      <HelpFooter>
        <FaInfoCircle aria-hidden="true" />
        <details><summary>處理方式與使用說明</summary><p>只有選「保留原圖 URL」才會將框選區域上傳至 Cloudflare 的「好題蒐集/」，並產生公開連結。框選時請排除姓名等個資。素材目前不自動到期，入題後請勿刪除引用中的圖片。</p><p>圖轉文字只辨識手動框選區域，其餘頁面保留整頁解析結果；請核對並修改文字後再加入題目。Python 轉換會使用 AI 額度，請核對圖形、標籤與比例。</p></details>
      </HelpFooter>
    </Workspace>
  );
}

const Workspace = styled.section`
  --figure-accent: #3974c4;
  --figure-tint: rgba(70, 124, 198, .09);
  container: handout-figures / inline-size;
  margin-top: 20px; min-width: 0; overflow: hidden;
  border: 1px solid var(--app-border, #dce3eb); border-radius: 16px;
  background: var(--app-surface, #fff); color: var(--app-text, #243247);
  font-size: 14px; line-height: 1.6; text-align: left;
  &, * { box-sizing: border-box; }
  h3, h4, p { margin: 0; }
  h4 { font-size: 14px; font-weight: 700; }
  p { overflow-wrap: anywhere; }
  fieldset { border: 0; margin: 0; padding: 0; min-width: 0; }
  button, select, input, textarea { font: inherit; }
  button { cursor: pointer; }
  button:disabled, fieldset:disabled button { cursor: not-allowed; }
  button:focus-visible, summary:focus-visible, a:focus-visible {
    outline: 3px solid #739cdd; outline-offset: 3px;
  }
  input, select, textarea {
    border: 1px solid var(--app-border, #cfd8e3); border-radius: 8px;
    background: var(--app-input-bg, #fff); color: var(--app-input-text, #243247);
    min-width: 0; width: 100%; padding: 10px 12px; line-height: 1.4;
    &:focus { outline: 2px solid #739cdd; outline-offset: 1px; }
    &:disabled { opacity: .6; }
  }
  textarea { min-height: 200px; margin-top: 12px; resize: vertical; font-family: ui-monospace, monospace; font-size: 13px; }
  @media (prefers-reduced-motion: reduce) { *, *::before, *::after { animation: none !important; transition: none !important; } }
`;
const WorkspaceHeader = styled.header`
  padding: 22px 24px 18px; display: flex; align-items: center; gap: 12px; flex-wrap: wrap;
  @container handout-figures (max-width: 420px) { padding: 18px 16px; gap: 10px; }
`;
const HeadingIcon = styled.div`
  width: 42px; height: 42px; display: grid; place-items: center; flex-shrink: 0;
  border-radius: 12px; background: var(--figure-tint); color: var(--figure-accent); font-size: 19px;
`;
const HeadingCopy = styled.div`
  flex: 1; min-width: 160px;
  h3 { font-size: 20px; font-weight: 750; letter-spacing: .02em; }
  p { font-size: 12px; color: var(--app-muted-text, #68778c); margin-top: 2px; }
`;
const Badge = styled.span`
  display: inline-flex; align-items: center; gap: 6px; flex-shrink: 0;
  border: 1px solid ${({ $active }) => $active ? "rgba(90, 137, 205, .24)" : "var(--app-border, #e0e5ed)"};
  border-radius: 20px; padding: 4px 9px; font-size: 11px; font-weight: 600;
  color: var(--app-subtle-text, #52627a); background: ${({ $active }) => $active ? "var(--figure-tint)" : "var(--app-surface-soft, #f7f9fb)"};
`;
const Spinner = styled(FaSyncAlt)`
  flex-shrink: 0; animation: figure-spin 1.1s linear infinite;
  @keyframes figure-spin { to { transform: rotate(360deg); } }
`;
const Steps = styled.ol`
  list-style: none; margin: 0 24px; padding: 0 0 18px; display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 8px;
  border-bottom: 1px solid var(--app-border, #e3e8ef);
  @container handout-figures (max-width: 420px) { margin: 0 16px; gap: 6px; }
`;
const Step = styled.li`
  display: flex; align-items: center; gap: 8px; font-size: 12px; line-height: 1.4;
  color: ${({ $active, $done }) => $active || $done ? "var(--app-text, #243247)" : "var(--app-muted-text, #7a8798)"};
  font-weight: ${({ $active }) => $active ? 700 : 500};
  > span { width: 25px; height: 25px; border-radius: 50%; display: grid; place-items: center; flex-shrink: 0; font-size: 10px;
    color: ${({ $active }) => $active ? "#fff" : "inherit"};
    background: ${({ $active, $done }) => $active ? "var(--figure-accent)" : $done ? "var(--figure-tint)" : "var(--app-surface-soft, #f0f3f7)"};
  }
  @container handout-figures (max-width: 420px) { flex-direction: column; align-items: start; gap: 6px; font-size: 11px; > span { width: 22px; height: 22px; } }
`;
const EmptyState = styled.div`
  display: flex; align-items: center; gap: 22px; padding: 32px 24px;
  margin: 20px 24px; border: 1px dashed var(--app-border, #ccd7e5); border-radius: 12px;
  background: var(--app-surface-soft, #f8fafc);
  h4 { font-size: 16px; margin-bottom: 6px; }
  p { max-width: 560px; color: var(--app-muted-text, #65758a); font-size: 13px; }
  @container handout-figures (max-width: 520px) { flex-direction: column; align-items: start; gap: 16px; padding: 22px; margin: 18px 16px; }
`;
const EmptyIcon = styled.div`
  position: relative; flex-shrink: 0; width: 64px; height: 72px; display: grid; place-items: center;
  border-radius: 12px; border: 1px solid var(--app-border, #dbe4ef); background: var(--app-surface, #fff);
  color: var(--figure-accent); box-shadow: -6px 6px 0 var(--figure-tint);
  > svg:first-child { font-size: 27px; opacity: .65; }
  > svg:last-child { position: absolute; right: -9px; bottom: 8px; font-size: 25px; background: var(--app-surface, #fff); border: 4px solid var(--app-surface, #fff); box-sizing: content-box; border-radius: 7px; }
`;
const FormatHint = styled.div`
  margin-top: 14px; color: var(--app-muted-text, #65758a); font-size: 12px; line-height: 1.9;
  code { display: inline-block; padding: 0 6px; border: 1px solid var(--app-border, #dbe3ed); border-radius: 4px; color: var(--app-subtle-text, #41536d); background: var(--app-surface, #fff); }
`;
const SetupGrid = styled.div`
  display: grid; grid-template-columns: minmax(0, 1fr); gap: 14px; padding: 20px 24px;
  @container handout-figures (min-width: 640px) { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  @container handout-figures (max-width: 420px) { padding: 18px 16px; }
`;
const SetupCard = styled.div`
  min-width: 0; padding: 16px; border: 1px solid var(--app-border, #e0e6ee); border-radius: 12px;
  display: flex; flex-direction: column; background: var(--app-surface, #fff);
`;
const CardHeading = styled.div`
  display: flex; gap: 8px; align-items: center; margin-bottom: 16px;
  > svg { color: var(--app-muted-text, #7589a2); font-size: 14px; }
`;
const Field = styled.label`
  display: flex; flex-direction: column; gap: 6px; min-width: 0;
  width: ${({ $narrow }) => $narrow ? "88px" : "100%"}; flex-shrink: 0;
  > span { color: var(--app-subtle-text, #586980); font-size: 12px; font-weight: 600; }
`;
const SelectWrap = styled.div`
  position: relative; min-width: 0;
  select { appearance: none; -webkit-appearance: none; padding-right: 32px; text-overflow: ellipsis; cursor: pointer; }
  > svg { position: absolute; right: 12px; top: 50%; transform: translateY(-50%); pointer-events: none; color: var(--app-muted-text, #738197); font-size: 10px; }
`;
const QuestionExcerpt = styled.p`
  margin: 10px 0 14px !important; font-size: 12px; color: var(--app-muted-text, #65758a);
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
`;
const FieldCaption = styled.span`
  display: block; font-size: 12px; font-weight: 600; color: var(--app-subtle-text, #586980); margin: auto 0 6px;
`;
const RoleGroup = styled.div`
  display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 3px; padding: 3px;
  background: var(--app-surface-soft, #f1f4f8); border: 1px solid var(--app-border, #e2e7ee); border-radius: 9px;
`;
const RoleButton = styled.button`
  border: 1px solid ${({ $selected }) => $selected ? "var(--app-border, #d6e0ed)" : "transparent"}; border-radius: 6px;
  background: ${({ $selected }) => $selected ? "var(--app-surface, #fff)" : "transparent"};
  color: ${({ $selected }) => $selected ? "var(--app-text, #294d7d)" : "var(--app-muted-text, #68778c)"};
  padding: 7px 8px; font-size: 12px !important; font-weight: ${({ $selected }) => $selected ? 700 : 500};
  box-shadow: ${({ $selected }) => $selected ? "0 1px 3px rgba(0, 0, 0, .07)" : "none"};
  &:hover:not(:disabled) { background: var(--figure-tint); }
`;
const SourceHint = styled.p`
  font-size: 12px; color: var(--app-muted-text, #68778c); margin: 10px 0 14px !important;
`;
const SourceActions = styled.div`
  display: flex; align-items: end; gap: 10px; margin-top: auto;
  > button { flex: 1; }
`;
const ActionButton = styled.button`
  display: inline-flex; align-items: center; justify-content: center; gap: 8px;
  min-height: 40px; padding: 10px 14px; border-radius: 8px; font-size: 13px !important; font-weight: 650; line-height: 1.4;
  border: 1px solid ${({ $primary }) => $primary ? "#3974c4" : "var(--app-border, #cad7e7)"};
  background: ${({ $primary }) => $primary ? "#3974c4" : "var(--figure-tint)"};
  color: ${({ $primary }) => $primary ? "#fff" : "var(--app-text, #305783)"};
  transition: background .15s, box-shadow .15s;
  &:hover:not(:disabled) { box-shadow: 0 0 0 3px var(--figure-tint); }
  &:disabled, fieldset:disabled & { opacity: .45; }
`;
const CropSection = styled.div`
  margin: 0 24px; border: 1px solid var(--app-border, #dce4ee); border-radius: 12px; overflow: hidden;
  @container handout-figures (max-width: 420px) { margin: 0 16px; }
`;
const SectionHeading = styled.div`
  display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 8px 12px; padding: 14px 16px;
  > div { display: flex; align-items: center; gap: 8px; }
  > div > svg { font-size: 13px; color: var(--app-muted-text, #70849d); }
  small { font-size: 11px; color: var(--app-muted-text, #6c7d92); }
`;
const CanvasStage = styled.div`
  padding: ${({ $loaded }) => $loaded ? "20px" : "0"}; min-height: 190px;
  border-top: 1px solid var(--app-border, #e0e7f0); border-bottom: 1px solid var(--app-border, #e0e7f0);
  background-color: var(--app-surface-soft, #f3f6fa);
  background-image: radial-gradient(var(--app-border, #d8e1ed) .8px, transparent .8px); background-size: 12px 12px;
  display: grid; place-items: center;
  @container handout-figures (max-width: 420px) { padding: ${({ $loaded }) => $loaded ? "10px" : "0"}; }
`;
const CanvasPlaceholder = styled.div`
  display: flex; flex-direction: column; align-items: center; gap: 7px; padding: 28px 16px; text-align: center;
  color: var(--app-muted-text, #78879b); font-size: 12px;
  > svg { font-size: 25px; margin-bottom: 5px; opacity: .65; }
  strong { color: var(--app-subtle-text, #576a83); font-size: 13px; }
`;
const CropFrame = styled.div`
  position: relative; display: block; width: fit-content; max-width: 100%; line-height: 0; overflow: hidden;
  touch-action: none; user-select: none; cursor: crosshair;
  box-shadow: 0 4px 20px rgba(0, 0, 0, .13);
  img { display: block; max-width: 100%; max-height: 600px; width: auto; }
`;
const Selection = styled.div`
  position: absolute; border: 2px solid #3974c4; background: rgba(57, 116, 196, .06); pointer-events: none;
  box-shadow: 0 0 0 9999px rgba(0, 0, 0, .32);
`;
const CropToolbar = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 10px; padding: 10px 16px; flex-wrap: wrap;
  font-size: 11px; color: var(--app-muted-text, #718097);
  strong { margin-left: 6px; font-weight: 600; font-variant-numeric: tabular-nums; color: var(--app-subtle-text, #435976); }
`;
const QuietButton = styled.button`
  border: 1px solid var(--app-border, #d6e0ec); background: var(--app-surface, #fff);
  color: var(--app-subtle-text, #4e6583); padding: 5px 10px; border-radius: 6px; font-size: 12px !important;
  &:hover:not(:disabled) { background: var(--figure-tint); }
`;
const Disclosure = styled.details`
  padding: 12px 16px; border-top: 1px solid var(--app-border, #e0e7f0); font-size: 12px;
  color: var(--app-muted-text, #667a95);
  summary { cursor: pointer; width: fit-content; } summary svg { margin: 0 6px; }
`;
const CropFields = styled.div`
  display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 10px; padding-top: 12px;
  @container handout-figures (max-width: 360px) { grid-template-columns: repeat(2, minmax(0, 1fr)); }
`;
const SaveSection = styled.div`
  padding: 6px 24px 20px;
  > div:first-child { padding-left: 0; padding-right: 0; }
  @container handout-figures (max-width: 420px) { padding: 6px 16px 18px; }
`;
const MethodGrid = styled.div`
  display: grid; grid-template-columns: minmax(0, 1fr); gap: 12px;
  @container handout-figures (min-width: 500px) { grid-template-columns: repeat(3, minmax(0, 1fr)); }
`;
const MethodCard = styled.button`
  min-width: 0; display: flex; flex-direction: column; text-align: left; padding: 16px; border-radius: 12px;
  border: 1px solid var(--app-border, #d9e3ef); background: var(--app-surface, #fff); color: var(--app-text, #243247);
  transition: border-color .15s, box-shadow .15s, background .15s;
  > strong { font-size: 15px; margin: 11px 0 5px; font-weight: 700; }
  > p { font-size: 12px; line-height: 1.7; color: var(--app-muted-text, #63758d); }
  &:hover:not(:disabled) { border-color: ${({ $python }) => $python ? "#9281c9" : "#729bd2"}; background: var(--figure-tint); box-shadow: 0 3px 12px rgba(0, 0, 0, .06); }
  &:disabled, fieldset:disabled & { background: var(--app-surface-soft, #f8fafc); opacity: .6; }
`;
const MethodTop = styled.div`display: flex; align-items: center; justify-content: space-between; gap: 8px; width: 100%;`;
const MethodIcon = styled.div`
  display: grid; place-items: center; width: 34px; height: 34px; border-radius: 9px; font-size: 15px;
  background: ${({ $python }) => $python ? "rgba(151, 122, 203, .13)" : "var(--figure-tint)"};
  color: ${({ $python }) => $python ? "#957aca" : "var(--figure-accent)"};
`;
const MethodTag = styled.span`font-size: 10px; color: var(--app-muted-text, #72839b); border: 1px solid var(--app-border, #e2e8f1); border-radius: 5px; padding: 2px 6px;`;
const MethodNote = styled.span`font-size: 10px; color: var(--app-muted-text, #71829a); display: block; margin: 10px 0 14px;`;
const MethodAction = styled.span`
  display: flex; justify-content: space-between; align-items: center; width: 100%; margin-top: auto; padding-top: 10px;
  border-top: 1px solid var(--app-border, #e2e8f0); font-size: 12px; font-weight: 650; color: var(--app-subtle-text, #45648d);
  > svg { font-size: 10px; }
`;
const StatusNotice = styled.div`
  display: flex; align-items: start; gap: 9px; margin: 0 24px 18px; padding: 12px 14px; border-radius: 9px; font-size: 12px;
  border: 1px solid ${({ $tone }) => $tone === "error" ? "rgba(196, 99, 84, .4)" : $tone === "success" ? "rgba(57, 153, 125, .3)" : "var(--app-border, #dce5f1)"};
  background: ${({ $tone }) => $tone === "error" ? "rgba(196, 99, 84, .08)" : $tone === "success" ? "rgba(57, 153, 125, .07)" : "var(--figure-tint)"};
  color: var(--app-subtle-text, #40556f); overflow-wrap: anywhere;
  > svg { flex-shrink: 0; margin-top: 3px; }
  @container handout-figures (max-width: 420px) { margin-left: 16px; margin-right: 16px; }
`;
const ReviewSection = styled.div`
  margin: 0 24px 20px; border: 1px solid rgba(87, 139, 194, .4); border-radius: 12px; overflow: hidden;
  background: var(--app-surface, #fff);
  > button { margin: 0 16px 16px; }
  > ${StatusNotice} { margin: 12px 16px; }
  @container handout-figures (max-width: 420px) { margin: 0 16px 18px; }
`;
const PreviewRow = styled.div`
  display: grid; grid-template-columns: repeat(auto-fit, minmax(min(100%, 200px), 1fr)); gap: 12px; padding: 0 16px 16px;
  figure { min-width: 0; margin: 0; border: 1px solid var(--app-border, #dee6f0); border-radius: 9px; overflow: hidden; }
  figcaption { font-size: 11px; padding: 8px 10px; background: var(--app-surface-soft, #f6f8fb); color: var(--app-muted-text, #6d7f98); }
  figure > div { display: grid; place-items: center; padding: 12px; background: white; min-height: 120px; }
  img { display: block; max-width: 100%; max-height: 320px; object-fit: contain; }
`;
const OriginalLink = styled.a`
  display: inline-flex; gap: 6px; align-items: center; color: var(--app-link-color, #4c80c7); font-size: 12px; margin: 0 16px 16px;
  > svg { font-size: 10px; }
`;
const ConfirmBar = styled.div`
  display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 14px; padding: 16px;
  border-top: 1px solid var(--app-border, #e0e7f0); background: var(--figure-tint);
  > div { display: flex; flex-direction: column; gap: 3px; flex: 1; min-width: 180px; }
  strong { font-size: 13px; } span { font-size: 11px; color: var(--app-muted-text, #6a7c94); }
  @container handout-figures (max-width: 480px) { > button { width: 100%; } }
`;
const HelpFooter = styled.div`
  display: flex; gap: 8px; align-items: start; padding: 13px 24px; border-top: 1px solid var(--app-border, #e2e8f0);
  background: var(--app-surface-soft, #f8fafc); color: var(--app-muted-text, #6c7d93); font-size: 11px;
  > svg { flex-shrink: 0; margin-top: 4px; }
  summary { cursor: pointer; width: fit-content; font-weight: 500; }
  p { margin-top: 10px; max-width: 650px; line-height: 1.8; }
  @container handout-figures (max-width: 420px) { padding: 12px 16px; }
`;
